import React, { useRef, useEffect } from 'react';
import { Modal, View, StyleSheet, Platform, ScrollView, Pressable } from 'react-native';
import DLOPrintSchedule from './DLOPrintSchedule';
import DLOButton from './DLOButton';

interface Schedule {
  'Loc ID': string;
  'YR': number;
  'PRD': number;
  'WK': number;
  'is_fav': boolean;
  'fav_name': string | null;
  'total_forecasted_cases': number | null;
  'total_scheduled_hours': number;
  'total_actual_hours': number;
  'recommended_hours': number | null;
  'CreatedAt': string;
  'CreatedBy': string | null;
  'ModifiedAt': string | null;
  'ModifiedBy': string | null;
}

interface EmployeeRow {
  id: string;
  employee: { id: string; value: string; label: string } | null;
  role: { id: string; value: string; label: string } | null;
  shift: { id: string; value: string; label: string } | null;
  sunday: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  total: string;
  isChildRow?: boolean;
}

interface DLOPrintModalProps {
  visible: boolean;
  onClose: () => void;
  scheduleDetails: Schedule | null;
  employeeRows: EmployeeRow[];
  locationName?: string;
  periodWeekYear: string;
  activeTab: 'schedule' | 'actual';
  hideHours?: boolean;
}

const DLOPrintModal: React.FC<DLOPrintModalProps> = ({
  visible,
  onClose,
  scheduleDetails,
  employeeRows,
  locationName,
  periodWeekYear,
  activeTab,
  hideHours = false
}) => {
  const printContentRef = useRef<View>(null);

  const handlePrint = () => {
    if (Platform.OS === 'web') {
      // For web, use browser print
      window.print();
    } else {
      // For mobile, we would need a library like react-native-print
      console.log('Print functionality for mobile needs to be implemented');
    }
  };

  // Add print event listeners for web
  useEffect(() => {
    if (Platform.OS === 'web' && visible) {
      // Inject print styles dynamically
      const styleId = 'dlo-print-styles';
      let styleElement = document.getElementById(styleId);
      
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        styleElement.innerHTML = `
          @media print {
            @page {
              size: portrait;
              margin: 0.5in;
            }

            body * {
              visibility: hidden;
            }
            
            #print-content,
            #print-content * {
              visibility: visible;
            }
            
            #print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white !important;
            }

            /* Ensure modal and scroll containers don't interfere with print */
            .contentScroll {
              overflow: visible !important;
              height: auto !important;
            }

            /* Hide action buttons during print */
            .actionBar,
            [class*="actionBar"] {
              display: none !important;
            }

            /* Preserve colors in print */
            #print-content * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Page breaks */
            .page-break-after {
              page-break-after: always !important;
              break-after: page !important;
            }

            .page-break-before {
              page-break-before: always !important;
              break-before: page !important;
            }
            
            /* Table styling */
            .table-container {
              page-break-inside: auto;
              width: 100%;
            }
            
            .table-row {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            
            /* Preserve background colors */
            .table-header {
              background-color: #005CBC !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .table-header * {
              color: white !important;
            }

            /* Remove shadows and unnecessary styles for print */
            * {
              box-shadow: none !important;
              text-shadow: none !important;
            }

            /* Ensure borders are visible */
            table, th, td {
              border-color: #E0E0E0 !important;
            }
          }
        `;
        document.head.appendChild(styleElement);
      }

      const handleBeforePrint = () => {
        console.log('Preparing to print...');
      };

      const handleAfterPrint = () => {
        console.log('Print completed or cancelled');
      };

      window.addEventListener('beforeprint', handleBeforePrint);
      window.addEventListener('afterprint', handleAfterPrint);

      return () => {
        window.removeEventListener('beforeprint', handleBeforePrint);
        window.removeEventListener('afterprint', handleAfterPrint);
      };
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      onRequestClose={onClose}
      animationType="fade"
      transparent={true}
    >
      <Pressable 
        style={styles.modalOverlay}
        onPress={onClose}
      >
        <Pressable 
          style={styles.modalContainer}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Print Content with Action Buttons Inside Scroll */}
          <ScrollView 
            style={styles.contentScroll}
            {...(Platform.OS === 'web' ? { className: 'contentScroll' } : {})}
          >
            {/* Action Buttons at Top */}
            <View 
              style={styles.actionBar}
              {...(Platform.OS === 'web' ? { className: 'actionBar' } : {})}
            >
              <DLOButton
                text="Print"
                variant="primary"
                size="medium"
                onPress={handlePrint}
              />
              <DLOButton
                text="Close"
                variant="secondary"
                size="medium"
                onPress={onClose}
                style={styles.closeButton}
              />
            </View>

            <View 
              ref={printContentRef} 
              style={styles.printContent}
              // @ts-ignore - id is for web print styling only
              {...(Platform.OS === 'web' ? { id: 'print-content' } : {})}
            >
              <DLOPrintSchedule
                scheduleDetails={scheduleDetails}
                employeeRows={employeeRows}
                locationName={locationName}
                periodWeekYear={periodWeekYear}
                activeTab={activeTab}
                hideHours={hideHours}
              />
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 1200,
    height: '90%',
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 24,
        elevation: 8,
      },
    }),
  },
  actionBar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F5F5F5',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    gap: 12,
    marginBottom: 0,
  },
  closeButton: {
    marginLeft: 12,
  },
  contentScroll: {
    flex: 1,
  },
  printContent: {
    padding: 20,
  },
});

export default DLOPrintModal;
